import { LayersFill } from "@procore/core-icons";
import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { store } from "app/store";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import { getBestAxisAlignedView } from "features/orthoCam/utils";
import {
    CameraType,
    renderActions,
    selectCameraType,
    selectDefaultVisibility,
    selectIsSubtreesPanelOpen,
    selectIsXRayActive,
} from "features/render";
import { SubtreesPopover } from "features/render/subtreesPopover";
import { undoRedoActions } from "features/undoRedo";
import OrthographicIcon from "media/icons/orthographic.svg?react";
import PerspectiveIcon from "media/icons/perspective.svg?react";
import XRayIcon from "media/icons/xray.svg?react";
import { telemetry } from "telemetry";
import { isMacintosh } from "utils/misc";

import { Toolbar } from "../Toolbar";

export function ViewToolbar({ id = "view-toolbar" }: { id?: string }) {
    const dispatch = useAppDispatch();
    const cameraType = useAppSelector(selectCameraType);
    const isXRayActive = useAppSelector(selectIsXRayActive);
    const isSubtreesPanelOpen = useAppSelector(selectIsSubtreesPanelOpen);
    const subtreesBtnRef = useRef<HTMLButtonElement>(null);
    const {
        state: { view, scene },
    } = useExplorerGlobals(true);
    const toolbarDispatch = useDispatchToolbars();

    const handleCameraToggle = useCallback(() => {
        if (!view || !scene) {
            return;
        }
        void track(Events.toolbar.cameraTapped, { origin: EventOrigin.Toolbar });

        if (cameraType === CameraType.Pinhole) {
            // Switch to orthographic with intelligent axis-aligned view selection
            getBestAxisAlignedView(view)
                .then((orthographicParams) => {
                    dispatch(
                        renderActions.setCamera({
                            type: CameraType.Orthographic,
                            goTo: orthographicParams,
                        }),
                    );
                })
                .catch((error) => {
                    console.warn("Failed to calculate best axis-aligned view:", error);
                    telemetry.trackCaughtException({
                        source: "ViewToolbar.cameraToggle",
                        category: "render",
                        error,
                    });
                    dispatch(
                        renderActions.setCamera({
                            type: CameraType.Orthographic,
                        }),
                    );
                });
        } else {
            dispatch(
                renderActions.setCamera({
                    type: CameraType.Pinhole,
                }),
            );
        }
    }, [dispatch, view, scene, cameraType]);

    const buttons: ToolbarButtonEntry[] = useMemo(
        () => [
            {
                config: {
                    id: "camera-toggle",
                    icon: cameraType === CameraType.Orthographic ? <PerspectiveIcon /> : <OrthographicIcon />,
                    onClick: () => handleCameraToggle(),
                },
                state: {
                    active: false,
                    disabled: !view || !scene,
                    visible: true,
                },
            },
            {
                config: {
                    id: "render-modes",
                    icon: <LayersFill />,
                    ref: subtreesBtnRef,
                    onClick: () => dispatch(renderActions.setSubtreesPanelOpen(!isSubtreesPanelOpen)),
                    tooltip: {
                        toolName: "renderModes",
                        shortcutKey: "",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: isSubtreesPanelOpen,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: "xray-toggle",
                    icon: <XRayIcon />,
                    onClick: () => {
                        const currentVisibility = selectDefaultVisibility(store.getState());
                        dispatch(renderActions.toggleXRayMode());
                        dispatch(
                            undoRedoActions.push({
                                description: "Toggle X-Ray mode",
                                undoCallback: () => dispatch(renderActions.setVisibilityMode(currentVisibility)),
                                redoCallback: () => dispatch(renderActions.toggleXRayMode()),
                            }),
                        );
                    },
                    tooltip: {
                        toolName: "xRay",
                        shortcutKey: isMacintosh ? "Cmd+X" : "Ctrl+X",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: isXRayActive,
                    disabled: !view || !scene,
                    visible: true,
                },
            },
        ],
        [cameraType, view, scene, handleCameraToggle, isXRayActive, isSubtreesPanelOpen, dispatch],
    );

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id,
                buttons,
            }),
        );

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(id));
        };
    }, [toolbarDispatch, id, buttons]);

    return (
        <>
            <Toolbar id={id} />
            <SubtreesPopover
                open={isSubtreesPanelOpen}
                anchorEl={subtreesBtnRef.current}
                onClose={() => dispatch(renderActions.setSubtreesPanelOpen(false))}
            />
        </>
    );
}
